/* Estilos del widget. Viven en el Shadow DOM: no salen a la página ni entran los de la página. */

/*
 * La página puede tener reglas que alcanzan a <aura-assistant> (`* { font-family: x !important }`)
 * y todo lo de adentro las hereda. Ante reglas normales gana la página; ante `!important` gana el
 * Shadow DOM: por eso el reset del host va con `!important`. Las variables `--aura-*` quedan sin
 * `!important` a propósito: la página puede cambiarlas para el theming.
 */
:host {
  all: initial !important;
  position: fixed !important;
  right: 20px !important;
  bottom: 20px !important;
  z-index: 2147483000 !important;
  display: block !important;
  font-family: var(--aura-font) !important;
  font-size: 15px !important;
  line-height: 1.5 !important;
  color: var(--aura-text) !important;
  --aura-primary: #2f5bea;
  --aura-primary-text: #ffffff;
  --aura-bg: #f6f7fb;
  --aura-surface: #ffffff;
  --aura-text: #1c2033;
  --aura-muted: #646b85;
  --aura-border: #e2e5ee;
  --aura-error-bg: #fdecec;
  --aura-error-text: #9b1c1c;
  --aura-radius: 16px;
  --aura-font: system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  --aura-gradient: linear-gradient(
    135deg,
    var(--aura-primary),
    color-mix(in srgb, var(--aura-primary) 70%, #6d28d9)
  );
  --aura-tint: color-mix(in srgb, var(--aura-primary) 10%, #ffffff);
  --aura-ease: cubic-bezier(0.16, 1, 0.3, 1);
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

[hidden] {
  display: none !important;
}

button {
  font: inherit;
  color: inherit;
  cursor: pointer;
}

:focus-visible {
  outline: 2px solid var(--aura-primary);
  outline-offset: 2px;
}

svg {
  display: block;
  flex: none;
}

/* ---------- Botón flotante ---------- */

.launcher {
  position: relative;
  display: grid;
  place-items: center;
  width: 58px;
  height: 58px;
  margin-left: auto;
  border: 0;
  border-radius: 50%;
  background: var(--aura-gradient);
  color: var(--aura-primary-text);
  box-shadow:
    0 8px 24px color-mix(in srgb, var(--aura-primary) 35%, transparent),
    0 2px 6px rgb(16 24 40 / 15%);
  transition:
    transform 0.25s var(--aura-ease),
    box-shadow 0.25s ease;
}

.launcher:hover {
  transform: translateY(-2px) scale(1.04);
  box-shadow:
    0 12px 28px color-mix(in srgb, var(--aura-primary) 45%, transparent),
    0 2px 6px rgb(16 24 40 / 15%);
}

.launcher:active {
  transform: scale(0.94);
}

/* El destello y la cruz se cruzan girando al abrir y cerrar. */
.launcher .icon {
  position: absolute;
  display: grid;
  place-items: center;
  transition:
    transform 0.3s var(--aura-ease),
    opacity 0.2s ease;
}

.launcher .icon-close {
  opacity: 0;
  transform: rotate(-90deg) scale(0.5);
}

:host(.open) .launcher .icon-open {
  opacity: 0;
  transform: rotate(90deg) scale(0.5);
}

:host(.open) .launcher .icon-close {
  opacity: 1;
  transform: none;
}

/* Un pulso suave cuando aparece la burbuja de bienvenida. */
.launcher.attention::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 50%;
  box-shadow: 0 0 0 0 color-mix(in srgb, var(--aura-primary) 55%, transparent);
  animation: aura-pulse 1.6s ease-out 2;
}

@keyframes aura-pulse {
  to {
    box-shadow: 0 0 0 16px transparent;
  }
}

/* ---------- Burbuja de bienvenida ---------- */

.teaser {
  position: absolute;
  right: 72px;
  bottom: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  width: max-content;
  max-width: 320px;
  padding: 10px 6px 10px 14px;
  border: 1px solid var(--aura-border);
  border-radius: 14px 14px 4px 14px;
  background: var(--aura-surface);
  box-shadow: 0 8px 24px rgb(16 24 40 / 14%);
  font-size: 14px;
  animation: aura-teaser-in 0.4s var(--aura-ease);
}

.teaser .teaser-text {
  border: 0;
  background: none;
  padding: 0;
  text-align: left;
  color: var(--aura-text);
}

.teaser .teaser-text strong {
  color: var(--aura-primary);
}

.teaser .teaser-close {
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border: 0;
  border-radius: 50%;
  background: none;
  color: var(--aura-muted);
}

.teaser .teaser-close:hover {
  background: var(--aura-bg);
}

.teaser.leaving {
  animation: aura-teaser-out 0.2s ease forwards;
}

@keyframes aura-teaser-in {
  from {
    opacity: 0;
    transform: translateX(12px) scale(0.96);
  }
}

@keyframes aura-teaser-out {
  to {
    opacity: 0;
    transform: translateX(8px);
  }
}

/* ---------- Panel ---------- */

.panel {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 384px;
  height: min(620px, calc(100vh - 110px));
  margin-bottom: 14px;
  overflow: hidden;
  border: 1px solid var(--aura-border);
  border-radius: 20px;
  background: var(--aura-bg);
  box-shadow:
    0 24px 60px rgb(16 24 40 / 22%),
    0 4px 12px rgb(16 24 40 / 8%);
  transform-origin: bottom right;
  animation: aura-panel-in 0.32s var(--aura-ease);
}

.panel.closing {
  animation: aura-panel-out 0.18s ease-in forwards;
}

@keyframes aura-panel-in {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.92);
  }
}

@keyframes aura-panel-out {
  to {
    opacity: 0;
    transform: translateY(12px) scale(0.95);
  }
}

header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 12px 14px 14px;
  background: var(--aura-gradient);
  color: var(--aura-primary-text);
}

.avatar {
  display: grid;
  place-items: center;
  flex: none;
  border-radius: 50%;
  background: var(--aura-gradient);
  color: var(--aura-primary-text);
}

header .avatar {
  width: 38px;
  height: 38px;
  background: rgb(255 255 255 / 20%);
  box-shadow: inset 0 0 0 1px rgb(255 255 255 / 25%);
}

header .names {
  flex: 1;
  min-width: 0;
}

header .title {
  margin: 0;
  font-size: 16px;
  font-weight: 650;
  line-height: 1.2;
}

header .subtitle {
  margin: 2px 0 0;
  overflow: hidden;
  font-size: 12.5px;
  line-height: 1.3;
  opacity: 0.85;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.icon-button {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  transition: background 0.15s ease;
}

header .icon-button:hover {
  background: rgb(255 255 255 / 18%);
}

/* ---------- Mensajes ---------- */

.messages {
  flex: 1;
  overflow-y: auto;
  padding: 18px 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.empty {
  margin: auto 0;
  text-align: center;
  color: var(--aura-muted);
  font-size: 14px;
  animation: aura-fade-up 0.4s var(--aura-ease);
}

.empty .avatar {
  width: 60px;
  height: 60px;
  margin: 0 auto 12px;
  box-shadow: 0 10px 24px color-mix(in srgb, var(--aura-primary) 30%, transparent);
  animation: aura-float 4s ease-in-out infinite;
}

.empty .greeting {
  display: block;
  margin-bottom: 4px;
  color: var(--aura-text);
  font-size: 18px;
  font-weight: 650;
}

.chips {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 18px;
  text-align: left;
}

.chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  border: 1px solid var(--aura-border);
  border-radius: 12px;
  background: var(--aura-surface);
  color: var(--aura-text);
  font-size: 13.5px;
  line-height: 1.35;
  text-align: left;
  opacity: 0;
  animation: aura-fade-up 0.35s var(--aura-ease) forwards;
  transition:
    border-color 0.15s ease,
    background 0.15s ease,
    transform 0.15s ease;
}

.chip::after {
  content: '→';
  color: var(--aura-primary);
  transition: transform 0.15s ease;
}

.chip:hover {
  border-color: color-mix(in srgb, var(--aura-primary) 40%, var(--aura-border));
  background: var(--aura-tint);
}

.chip:hover::after {
  transform: translateX(3px);
}

.chip:nth-child(2) {
  animation-delay: 0.06s;
}

.chip:nth-child(3) {
  animation-delay: 0.12s;
}

@keyframes aura-float {
  50% {
    transform: translateY(-4px);
  }
}

@keyframes aura-fade-up {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

.msg {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  animation: aura-fade-up 0.28s var(--aura-ease);
}

.msg.user {
  justify-content: flex-end;
  animation-name: aura-msg-user;
}

@keyframes aura-msg-user {
  from {
    opacity: 0;
    transform: translateX(14px) scale(0.98);
  }
}

.msg .avatar {
  width: 28px;
  height: 28px;
}

.bubble {
  max-width: 85%;
  padding: 10px 14px;
  border-radius: var(--aura-radius);
  background: var(--aura-surface);
  border: 1px solid var(--aura-border);
  overflow-wrap: anywhere;
}

.assistant .bubble {
  border-bottom-left-radius: 6px;
  box-shadow: 0 1px 2px rgb(16 24 40 / 5%);
}

.user .bubble {
  border: 0;
  border-bottom-right-radius: 6px;
  background: var(--aura-gradient);
  color: var(--aura-primary-text);
  white-space: pre-wrap;
}

.no-info .bubble {
  color: var(--aura-muted);
}

.bubble p,
.bubble ul,
.bubble ol,
.bubble pre,
.bubble blockquote {
  margin: 0 0 8px;
}

.bubble .body > :last-child {
  margin-bottom: 0;
}

.bubble ul,
.bubble ol {
  padding-left: 20px;
}

.bubble li + li {
  margin-top: 2px;
}

.bubble a {
  color: var(--aura-primary);
}

.bubble code {
  padding: 1px 5px;
  border-radius: 5px;
  background: var(--aura-bg);
  font-size: 13px;
}

.cite {
  display: inline-block;
  min-width: 17px;
  margin-left: 2px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--aura-tint);
  color: var(--aura-primary);
  font-size: 10.5px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  vertical-align: 2px;
  cursor: default;
}

.cite::after {
  content: attr(data-n);
}

/* Mientras llega el texto, un cursor al final. */
.caret {
  display: inline-block;
  width: 2px;
  height: 1em;
  margin-left: 2px;
  background: var(--aura-primary);
  vertical-align: -2px;
  animation: aura-blink 0.9s steps(2) infinite;
}

@keyframes aura-blink {
  50% {
    opacity: 0;
  }
}

/* Mientras busca: tres puntos que saltan. */
.typing .bubble {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--aura-muted);
  font-size: 13.5px;
}

.dots {
  display: inline-flex;
  gap: 4px;
}

.dots i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: color-mix(in srgb, var(--aura-primary) 55%, var(--aura-border));
  animation: aura-bounce 1.2s ease-in-out infinite;
}

.dots i:nth-child(2) {
  animation-delay: 0.15s;
}

.dots i:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes aura-bounce {
  0%,
  60%,
  100% {
    transform: none;
    opacity: 0.5;
  }
  30% {
    transform: translateY(-5px);
    opacity: 1;
  }
}

.sources {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid var(--aura-border);
  color: var(--aura-muted);
  font-size: 12px;
  animation: aura-fade-up 0.3s var(--aura-ease);
}

.sources .label {
  margin-bottom: 4px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.sources ol {
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: source;
}

.sources li {
  display: flex;
  gap: 6px;
  align-items: flex-start;
  padding: 3px 0;
  counter-increment: source;
}

.sources li::before {
  content: counter(source);
  flex: none;
  min-width: 17px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--aura-tint);
  color: var(--aura-primary);
  font-size: 10.5px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
}

.actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 6px;
  animation: aura-fade-up 0.3s var(--aura-ease);
}

.copy {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border: 0;
  border-radius: 8px;
  background: none;
  color: var(--aura-muted);
  font-size: 12px;
  transition:
    background 0.15s ease,
    color 0.15s ease;
}

.copy:hover {
  background: var(--aura-bg);
  color: var(--aura-text);
}

.copy.done {
  color: #138a52;
}

.error {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 14px;
  border-radius: var(--aura-radius);
  background: var(--aura-error-bg);
  color: var(--aura-error-text);
  font-size: 14px;
  animation: aura-shake 0.4s ease;
}

.error button {
  display: block;
  margin-top: 8px;
  padding: 5px 12px;
  border: 1px solid currentColor;
  border-radius: 999px;
  background: none;
  font-size: 13px;
  font-weight: 600;
}

.error button:hover {
  background: rgb(255 255 255 / 50%);
}

@keyframes aura-shake {
  20%,
  60% {
    transform: translateX(-4px);
  }
  40%,
  80% {
    transform: translateX(4px);
  }
}

/* Volver abajo si el usuario subió mientras llegaba la respuesta. */
.scroll-down {
  position: absolute;
  left: 50%;
  bottom: 84px;
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border: 1px solid var(--aura-border);
  border-radius: 50%;
  background: var(--aura-surface);
  color: var(--aura-text);
  box-shadow: 0 6px 16px rgb(16 24 40 / 14%);
  transform: translateX(-50%);
  animation: aura-fade-up 0.2s ease;
}

/* ---------- Composer ---------- */

.composer {
  padding: 12px;
  border-top: 1px solid var(--aura-border);
  background: var(--aura-surface);
}

.field {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  padding: 5px 5px 5px 14px;
  border: 1px solid var(--aura-border);
  border-radius: 22px;
  background: var(--aura-bg);
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.field:focus-within {
  border-color: color-mix(in srgb, var(--aura-primary) 55%, var(--aura-border));
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--aura-primary) 15%, transparent);
}

.composer textarea {
  flex: 1;
  max-height: 140px;
  min-height: 36px;
  resize: none;
  padding: 7px 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--aura-text);
  font: inherit;
  line-height: 1.45;
}

.composer textarea::placeholder {
  color: var(--aura-muted);
}

.composer .send {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 50%;
  background: var(--aura-gradient);
  color: var(--aura-primary-text);
  transition:
    transform 0.15s ease,
    opacity 0.15s ease;
}

.composer .send:hover:not(:disabled) {
  transform: scale(1.06);
}

.composer .send:active:not(:disabled) {
  transform: scale(0.9);
}

.composer .send:disabled {
  opacity: 0.45;
  cursor: default;
}

/* Al mandar, la flecha sale volando y vuelve. */
.composer .send.sent svg {
  animation: aura-send 0.45s var(--aura-ease);
}

@keyframes aura-send {
  45% {
    transform: translateY(-14px);
    opacity: 0;
  }
  46% {
    transform: translateY(12px);
    opacity: 0;
  }
}

.hint {
  margin: 6px 0 0;
  color: var(--aura-muted);
  font-size: 11px;
  text-align: center;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

/* ---------- Página de chat (data-mode="page") ---------- */

:host(.page) {
  inset: 0 !important;
}

:host(.page) .launcher,
:host(.page) .teaser,
:host(.page) .close {
  display: none !important;
}

:host(.page) .panel {
  width: 100%;
  height: 100dvh;
  margin: 0;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  animation: none;
}

/* El contenido en una columna centrada; la barra de scroll queda en el borde de la ventana. */
:host(.page) header,
:host(.page) .messages,
:host(.page) .composer {
  padding-left: max(16px, calc((100% - 820px) / 2));
  padding-right: max(16px, calc((100% - 820px) / 2));
}

:host(.page) .bubble {
  max-width: 78%;
}

/* Sin clave: el formulario en lugar de la conversación. */
.locked .messages > :not(.key-form),
.locked .composer,
.locked .scroll-down {
  display: none !important;
}

.key-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: 340px;
  margin: auto;
  text-align: center;
  color: var(--aura-muted);
  font-size: 14px;
  animation: aura-fade-up 0.4s var(--aura-ease);
}

.key-form .avatar {
  width: 60px;
  height: 60px;
  box-shadow: 0 10px 24px color-mix(in srgb, var(--aura-primary) 30%, transparent);
}

.key-form .key-title {
  color: var(--aura-text);
  font-size: 18px;
  font-weight: 650;
}

.key-form p {
  margin: 0;
}

.key-form input {
  width: 100%;
  padding: 11px 14px;
  border: 1px solid var(--aura-border);
  border-radius: 12px;
  background: var(--aura-surface);
  color: var(--aura-text);
  font: inherit;
  outline: 0;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.key-form input:focus {
  border-color: color-mix(in srgb, var(--aura-primary) 55%, var(--aura-border));
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--aura-primary) 15%, transparent);
}

.key-form .key-error {
  color: var(--aura-error-text);
  font-weight: 600;
}

.key-form button {
  width: 100%;
  padding: 11px 14px;
  border: 0;
  border-radius: 12px;
  background: var(--aura-gradient);
  color: var(--aura-primary-text);
  font-weight: 650;
}

/* ---------- Celular: el panel ocupa toda la pantalla ---------- */

@media (max-width: 479px) {
  :host(.open) {
    inset: 0 !important;
  }

  :host(.open) .launcher,
  :host(.open) .teaser {
    display: none;
  }

  .panel {
    width: 100vw;
    height: 100dvh;
    margin: 0;
    border: 0;
    border-radius: 0;
    transform-origin: bottom center;
  }

  .teaser {
    max-width: calc(100vw - 110px);
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.001ms !important;
    animation-iteration-count: 1 !important;
    animation-delay: 0s !important;
    transition-duration: 0.001ms !important;
    scroll-behavior: auto !important;
  }

  .caret {
    animation: none !important;
  }
}
